/* eslint-disable max-len */
/* eslint-disable complexity */
import { declensionByNumber } from '@search/helpers/src/declensionByNumber';
import { DealType } from '@search/filter-enums/enums/DealType';
import { RealtyObjectType } from '@search/filter-enums/enums/RealtyObjectType';
import { OffersSummary } from '@search/graphql-typings';
import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { mskCity } from '@search/offer-search-routes/src/mappers/regions/msk/mskCityMapper';
import type { ISearchType } from '@search/offer-search-routes/src';
import { seoPlaceUrlParams } from '@search/offer-search-routes/src/seoPlaceUrlParams';
import { PriceSeoMatcher } from '@search/offer-search-routes/src/searchRoute/PriceSeoMatcher';

export function seoSearchCommonJoin(...strings: readonly (string | undefined | null)[]) {
    return strings.filter(Boolean).join(' ');
}

export type SeoSearchCommonProps = {
    geos: readonly Parameters<typeof seoPlaceUrlParams>[0]['geos'][number][];
    params: {
        region: number;
        dealType?: DealType;
        categoryType?: RealtyObjectType;
        segments?: ISearchType['segments'];
        commisionType?: string;
        depositType?: string;
        livingTenantOptions?: Array<string>;
        metroBranch: number;
    };
    region?: {
        id: number;
        translit?: string | null;
        locative?: string | null;
        displayName?: string | null;
    };
    offersCount?: number;
    offersSummary?: OffersSummary;
};

export function seoSearchCommon({ params, geos, offersCount, offersSummary, region }: SeoSearchCommonProps) {
    const segment = params.segments;
    let dealTypeText = {
        noun: 'продажа',
        prepositional: 'продаже',
        genitive: 'продажи',
        verb: 'купить',
        proposal: 'купите'
    };

    if (params.dealType === DealType.RENT) {
        dealTypeText = {
            noun: 'аренда',
            prepositional: 'аренде',
            genitive: 'аренды',
            verb: 'снять',
            proposal: 'снимите'
        };
    }

    const {
        segmentGeos
    } = seoPlaceUrlParams({
        geos,
        regionIdSuggested: region?.id
    });

    const offersCountText = ! offersCount ? 'объявления' : `${
        offersCount}\u00A0${declensionByNumber(offersCount, [ 'объявление', 'объявления', 'объявлений' ])}`;

    const metro = segmentGeos.find(geo => geo.kind === 'METRO');

    const geoTexts: string[] = [];

    if (metro?.title) geoTexts.push(`у\u00A0${metro.title}`);

    const districtGeos = segmentGeos.filter(geo => geo.kind === 'STREET' || geo.kind === 'DISTRICT' || geo.kind === 'LOCALITY');
    let regionText = segment?.districtIds?.[0] === 390 ? 'в Зеленограде' : region?.locative;
    let additionalRegionText = '';
    let regionTextAlt = regionText;

    for (const districtGeo of districtGeos) {
        if (districtGeo?.kind === 'LOCALITY' && districtGeo?.locative) {
            geoTexts.push(districtGeo.locative);
            regionText = '';
            regionTextAlt = '';
        }

        if (districtGeo?.kind === 'STREET' && districtGeo?.locative) {
            geoTexts.push(districtGeo.locative);
            if (districtGeo?.locality?.locative) regionText = districtGeo.locality.locative;
            additionalRegionText = `\u00A0(${region?.displayName})`;
        }

        if (districtGeo?.kind === 'DISTRICT') {
            const isLocative = ! districtGeo.title?.startsWith('район');

            if (
                districtGeo.id === mskCity['novaya-moskva'] ||
                (isLocative && params.region === RegionIdEnum.MSK_OBL)
            ) {
                regionTextAlt = '';
                regionText = '';
            }

            geoTexts.push(isLocative || ! districtGeo.title ?
                (districtGeo.locative ?? '') :
                `в\u00A0районе ${
                    districtGeo.title.replace(/(район|округ|городской|административный|посёлок)/g, '')}`
            );
        }
    }

    const geoText = seoSearchCommonJoin(...geoTexts);
    let nearMetroText = '';

    if (
        segment?.metroIds?.length === 0 &&
        // (segment?.districtIds?.length === 0 || geoText.includes('округ')) &&
        segment?.transport === 'FOOT' &&
        segment?.transportTimeLimit === 'MAX_15_MINUTES'
    ) {
        nearMetroText = 'рядом с\u00A0метро';
    }

    const faqPricePeriod = params.dealType === DealType.RENT ? '/мес.' : '';
    const faqPrepositionDealPrepositionalText = params.dealType === DealType.RENT ? 'об' : 'о';

    const faqPricePerUnitText = offersSummary?.formattedPricePerUnitRange ?
        `, цена квадратного метра\u00A0— ${offersSummary?.formattedPricePerUnitRange}` :
        '';
    const faqPricePerUnitTextAlternative = offersSummary?.formattedPricePerUnitRange ?
        `Цена за ${params.categoryType === RealtyObjectType.LAND ? 'сотку' : 'м²'}\u00A0${offersSummary?.formattedPricePerUnitRange}` :
        '';
    const faqAreaHousingText = params.categoryType === RealtyObjectType.LAND ?
        'Средняя цена участка' :
        'Средняя цена жилья';

    const isStandalone = segment?.houseCategory?.[0] === 'STANDALONE';

    function generateFaqTexts({
        prefixSegmentText,
        segmentText,
        segmentFullText,
        commonPostfixText,
        additionalPostfixText
    }: {
        prefixSegmentText?: { plural: string; accusative: string; nominative: string; genitive: string };
        segmentText: { plural: string; accusative: string; nominative: string; genitive: string };
        segmentFullText?: { plural: string; accusative: string; nominative: string; genitive: string };
        commonPostfixText?: string;
        additionalPostfixText?: { plural: string; accusative: string };
    }) {
        const commonPlural = seoSearchCommonJoin(
            prefixSegmentText?.plural,
            segmentFullText?.plural ?? segmentText.plural,
            additionalPostfixText?.plural,
            commonPostfixText
        );
        const commonAccusative = seoSearchCommonJoin(
            dealTypeText.verb,
            prefixSegmentText?.accusative,
            segmentText.accusative,
            isStandalone ? 'для постоянного проживания' : undefined,
            additionalPostfixText?.accusative,
            commonPostfixText,
        );

        const rentCommonGenitiveText = seoSearchCommonJoin(
            'Стоимость аренды',
            prefixSegmentText?.genitive,
            segmentText.genitive,
            additionalPostfixText?.plural,
            commonPostfixText
        );
        const rentTextAlternative = `Минимальная стоимость ${offersSummary?.formattedPriceFrom}${faqPricePeriod}`;
        const questionText = params.dealType === DealType.RENT ?
            rentCommonGenitiveText :
            `Сколько стоит ${seoSearchCommonJoin(
                prefixSegmentText?.nominative,
                segmentText.nominative,
                isStandalone ? 'для постоянного проживания' : undefined,
                additionalPostfixText?.plural,
                commonPostfixText
            )}?`;

        const commonAnswerEndText = `Площадь составляет ${
            offersSummary?.formattedAreaRange}\u00A0кв.\u00A0м.${faqPricePerUnitText} ${faqAreaHousingText}\u00A0— ${
            offersSummary?.formattedAveragePrice}${faqPricePeriod}`;

        let answerText = `Самый большой выбор объектов недвижимости с\u00A0разной стоимостью\u00A0— цена ${offersSummary?.formattedPriceRange}${faqPricePeriod} ${commonAnswerEndText}`;

        if (params.dealType === DealType.RENT) answerText = `${rentCommonGenitiveText} начинается ${offersSummary?.formattedPriceFrom}${faqPricePeriod} ${commonAnswerEndText}`;
        if (params.dealType === DealType.SELL && params.categoryType === RealtyObjectType.FLAT && segmentFullText) {
            const narrowText = seoSearchCommonJoin(
                prefixSegmentText?.plural,
                segmentFullText?.plural ?? segmentText.plural,
            );

            answerText = `Самый большой выбор ${narrowText} с\u00A0разной стоимостью\u00A0— цена ${offersSummary?.formattedPriceRange}${faqPricePeriod} ${commonAnswerEndText}`;
        }

        const answerTextAlternative = {
            price: params.dealType === DealType.RENT ?
                rentTextAlternative :
                `Цена недвижимости ${offersSummary?.formattedPriceRange}`,
            averagePrice: `${params.dealType === DealType.RENT ? 'Средняя цена аренды' : faqAreaHousingText} — ${offersSummary?.formattedAveragePrice}${faqPricePeriod}`,
            area: `Площадь ${params.dealType === DealType.RENT ? 'жилья ' : ''}${offersSummary?.formattedAreaRange}\u00A0м²`,
            pricePerUnit: params.dealType === DealType.RENT ? null : faqPricePerUnitTextAlternative
        };

        return [
            {
                title: `🏠 Как ${commonAccusative} на\u00A0сайте m2.ru?`,
                text: `Ищите объявления ${faqPrepositionDealPrepositionalText}\u00A0${
                    dealTypeText.prepositional} ${
                    commonPlural}? Воспользуйтесь фильтрами или\u00A0поиском в\u00A0разделе. Сохраните понравившийся поиск и\u00A0получайте по\u00A0нему свежие обновления!`,
                points: [
                    `✅ Ищите объявления ${faqPrepositionDealPrepositionalText}\u00A0${
                        dealTypeText.prepositional} ${
                        commonPlural}?`,
                    '✅ Воспользуйтесь фильтрами или\u00A0поиском в\u00A0разделе.',
                    '✅ Сохраните понравившийся поиск и\u00A0получайте по\u00A0нему свежие обновления!'
                ]
            },
            {
                title: `💸 ${questionText}`,
                text: answerText,
                alternativeText: answerTextAlternative
            },
            {
                title: 'Почему стоит искать недвижимость у\u00A0нас?',
                text: `Предложения на\u00A0m2.ru\u00A0— только от\u00A0проверенных агентств недвижимости, частных лиц и\u00A0официальных застройщиков. У\u00A0нас самый большой выбор ${
                    commonPlural}. В\u00A0разделе ${
                    offersCount ? `размещено ${offersCountText}` : `размещены ${offersCountText}`
                } ${
                    offersSummary?.ratingValue ? `со\u00A0средним рейтингом\u00A0— ${offersSummary?.ratingValue}` : 'по\u00A0заданным параметрам'
                }. Гарантия сделки\u00A0— вернём полную стоимость недвижимости, если что-то пойдёт не\u00A0так.`
            }
        ];
    }

    let priceMaxText = '';

    if (params.dealType === DealType.RENT) {
        priceMaxText = segment?.priceMax ? `до\u00A0${Math.floor(segment.priceMax / 1e3)}\u00A0тыс` : '';
    }

    if (params.dealType === DealType.SELL) {
        priceMaxText = segment?.priceMax ? `до\u00A0${Math.floor(segment.priceMax / 1e6)}\u00A0млн` : '';
    }

    let parkingText = '';

    if (segment?.parkings?.some(v => v === 'CLOSED' || v === 'OPEN')) parkingText = 'с\u00A0парковкой';
    if (segment?.parkings?.[0] === 'UNDERGROUND') parkingText = 'с\u00A0подземной парковкой';

    let seoMortgage = '';

    if (segment?.seoMortgageAllowed) seoMortgage = 'в\u00A0ипотеку';

    let rentPaymentText = '';

    if (segment?.rentPrepayment?.includes('NO_PREPAYMENT')) rentPaymentText = 'без\u00A0предоплаты';

    const conciergeText = segment?.infrastructure?.includes('CONCIERGE') ? 'с\u00A0консьержем' : undefined;
    const hasLiftText = segment?.hasLift ? 'с\u00A0лифтом' : undefined;
    const balconyText = segment?.balcony ? 'с\u00A0балконом' : undefined;

    const minPriceText = offersSummary?.formattedPriceFrom ? `по цене ${offersSummary?.formattedPriceFrom}` : '';

    const isNonCapitalRegion = region?.id !== RegionIdEnum.MSK_AND_MSK_OBL &&
                     region?.id !== RegionIdEnum.SPB_AND_LEN_OBL &&
                     region?.id !== RegionIdEnum.MSK &&
                     region?.id !== RegionIdEnum.SPB &&
                     region?.id !== RegionIdEnum.MSK_OBL &&
                     region?.id !== RegionIdEnum.LEN_OBL;

    const priceSeo = PriceSeoMatcher.$.match({
        categoryType: params.categoryType ?? RealtyObjectType.FLAT,
        dealType: params.dealType as DealType ?? DealType.SELL,
        priceMax: segment?.priceMax,
        priceMin: segment?.priceMin,
        region: params.region,
        regionTranslit: region?.translit
    });

    return {
        conciergeText,
        hasLiftText,
        balconyText,

        rentPaymentText,
        seoMortgage,
        parkingText,
        dealTypeText,
        offersCountText,
        regionText,
        regionTextAlt,
        additionalRegionText,
        geoText,
        priceMaxText,
        nearMetroText,
        generateFaqTexts,
        minPriceText,
        isNonCapitalRegion,
        priceSeo
    };
}
